import { useEffect, useMemo, useRef, useState } from "react";
import { Download, FileSpreadsheet, Mail } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatEur, sklonuj } from "@/lib/format";
import { nazovVarianty } from "@/lib/variantyZakazky";
import type { Celok, Varianta, Zostava } from "@/api/schemas";
import { skupinyZostav } from "@/lib/celky";

export type DokumentSoZostavami = "ponuka" | "dodaci-list";

/** Čo ide do dokumentu — `zostavy = null`, keď sú vybraných variant všetky. */
export interface VyberDokumentu {
  varianty: string[];
  zostavy: string[] | null;
}

const TEXTY: Record<
  DokumentSoZostavami,
  { titul: string; popis: string; doCoho: string }
> = {
  ponuka: {
    titul: "Export cenovej ponuky",
    popis:
      "Vyber zostavy, ktoré majú ísť do ponuky — súhrn cien sa prepočíta len z vybraných.",
    doCoho: "do ponuky",
  },
  "dodaci-list": {
    titul: "Dodací list",
    popis: "Vyber zostavy, ktoré sa odovzdávajú — dodací list je bez cien.",
    doCoho: "do dodacieho listu",
  },
};

function aktivne(v: Varianta): Zostava[] {
  return ((v.zostavy ?? []) as Zostava[]).filter(
    (z) => !("deleted_at" in z) || !z.deleted_at,
  );
}

/** Zaškrtávacie pole s tretím stavom (časť má vybranú len niečo). */
function Zaskrtnutie({
  stav,
  onChange,
  label,
}: {
  stav: boolean | "ciastocne";
  onChange: () => void;
  label: string;
}): JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = stav === "ciastocne";
  }, [stav]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={stav === true}
      onChange={onChange}
      aria-label={label}
      className="h-4 w-4 accent-primary"
    />
  );
}

/**
 * Výber zostáv do PDF cenovej ponuky — default všetky zaškrtnuté;
 * do ponuky (aj súhrnu cien) idú len vybrané. Od KOLA 185 aj pre dodací
 * list (user: „výber ako pri ponuke“).
 *
 * Od KOLA 206 pri zákazke s viac variantami strom **varianta → zostavy**:
 * zaškrtnutie varianty zapne jej zostavy, predvolená je otvorená záložka.
 * Viac variant = jeden dokument, každá časť ako oddiel a na konci súčet.
 */
export function PonukaExportDialog({
  open,
  onOpenChange,
  varianty,
  otvorenaId,
  onExport,
  onExcel,
  onMail,
  exporting,
  dokument = "ponuka",
  predvolene,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Varianty v poradí záložiek; pri jednej je okno ako do KOLA 206. */
  varianty: Varianta[];
  otvorenaId: string;
  onExport: (vyber: VyberDokumentu) => void;
  /** Ponuka v Exceli (KOLO 226) — bez neho tlačidlo nie je. */
  onExcel?: (vyber: VyberDokumentu) => void;
  /** Koncept e-mailu pre Outlook s PDF (a voliteľne Excelom) — KOLO 226. */
  onMail?: (vyber: VyberDokumentu, sExcelom: boolean) => void;
  exporting: boolean;
  dokument?: DokumentSoZostavami;
  /**
   * Varianty vybrané po otvorení (KOLO 230: aktívny nábytok + aktívny tovar
   * — jedna ponuka z oboch); bez neho otvorená záložka.
   */
  predvolene?: string[];
}): JSX.Element {
  const texty = TEXTY[dokument];
  const strom = varianty.length > 1;
  const zostavy = useMemo(
    () => new Map(varianty.map((v) => [v.id, aktivne(v)])),
    [varianty],
  );
  const [vybrane, setVybrane] = useState<Set<string>>(new Set());
  // Do e-mailu aj Excel — kolega u zákazníka si ceny skopíruje (KOLO 226)
  const [sExcelom, setSExcelom] = useState(true);
  // Varianta bez zostáv sa vyberá sama — inak by sa nedala zaškrtnúť
  const [prazdne, setPrazdne] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    const ids = (predvolene ?? []).filter((id) => zostavy.has(id));
    const zaciatok = ids.length > 0 ? ids : [otvorenaId];
    setVybrane(
      new Set(zaciatok.flatMap((id) => (zostavy.get(id) ?? []).map((z) => z.id))),
    );
    setPrazdne(
      new Set(zaciatok.filter((id) => (zostavy.get(id) ?? []).length === 0)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function prepniZostavu(id: string): void {
    setVybrane((pred) => {
      const nove = new Set(pred);
      if (nove.has(id)) nove.delete(id);
      else nove.add(id);
      return nove;
    });
  }

  function stavVarianty(v: Varianta): boolean | "ciastocne" {
    const jej = zostavy.get(v.id) ?? [];
    if (jej.length === 0) return prazdne.has(v.id);
    const pocet = jej.filter((z) => vybrane.has(z.id)).length;
    if (pocet === 0) return false;
    return pocet === jej.length ? true : "ciastocne";
  }

  function prepniVariantu(v: Varianta): void {
    const jej = zostavy.get(v.id) ?? [];
    if (jej.length === 0) {
      setPrazdne((pred) => {
        const nove = new Set(pred);
        if (nove.has(v.id)) nove.delete(v.id);
        else nove.add(v.id);
        return nove;
      });
      return;
    }
    const zapnut = stavVarianty(v) !== true;
    setVybrane((pred) => {
      const nove = new Set(pred);
      for (const z of jej) {
        if (zapnut) nove.add(z.id);
        else nove.delete(z.id);
      }
      return nove;
    });
  }

  const vybraneVarianty = varianty.filter((v) => stavVarianty(v) !== false);
  const vsetkyZostavy = vybraneVarianty.every(
    (v) => stavVarianty(v) === true,
  );
  const pocetVsetkych = vybraneVarianty.reduce(
    (s, v) => s + (zostavy.get(v.id) ?? []).length,
    0,
  );
  const nic = vybraneVarianty.length === 0;
  const bezCeny = vybraneVarianty
    .flatMap((v) => (v.zostavy ?? []).flatMap((z) => z.dielce ?? []))
    .filter((d) => d.vlastna_nakup != null && Number(d.vlastna_nakup) === 0).length;

  /**
   * Zostavy varianty pod nadpismi celkov (KOLO 235) — ako v ponuke:
   * bez celku navrchu, potom „Kuchyňa" a jej zostavy.
   */
  function zostavyVarianty(v: Varianta): JSX.Element[] {
    return skupinyZostav(zostavy.get(v.id) ?? [], (v.celky ?? []) as Celok[])
      .filter((s) => s.zostavy.length > 0)
      .flatMap((s) => [
        ...(s.celok
          ? [
              <div
                key={`celok-${s.celok.id}`}
                className="px-1 pt-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                {s.celok.nazov}
              </div>,
            ]
          : []),
        ...s.zostavy.map(riadokZostavy),
      ]);
  }

  function riadokZostavy(z: Zostava): JSX.Element {
    return (
      <label
        key={z.id}
        className="flex cursor-pointer items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm hover:bg-accent"
      >
        <span className="flex min-w-0 items-center gap-2">
          <input
            type="checkbox"
            checked={vybrane.has(z.id)}
            onChange={() => prepniZostavu(z.id)}
            aria-label={`Zostava ${z.kod} ${texty.doCoho}`}
            className="h-4 w-4 accent-primary"
          />
          <span className="truncate">
            <span className="font-mono text-xs text-muted-foreground">
              {z.kod}
            </span>{" "}
            {z.nazov}
            {z.popis ? (
              <span className="text-muted-foreground"> · {z.popis}</span>
            ) : null}
          </span>
        </span>
        <span className="shrink-0 font-mono text-xs text-muted-foreground">
          {/* Cena ponuky (KOLO 231) — s koeficientom, ako v PDF */}
          {z.pocet_ks}×{(z.cena_celkom_ponuky ?? z.cena_celkom) != null
            ? ` · ${formatEur(z.cena_celkom_ponuky ?? z.cena_celkom)}`
            : ""}
        </span>
      </label>
    );
  }

  function vyberDokumentu(): VyberDokumentu {
    return {
      varianty: vybraneVarianty.map((v) => v.id),
      zostavy: vsetkyZostavy ? null : [...vybrane],
    };
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{texty.titul}</DialogTitle>
          <DialogDescription className="sr-only">
            Výber zostáv, ktoré pôjdu {texty.doCoho}.
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {texty.popis}
          {strom
            ? " Viac variant = jeden dokument, každá ako oddiel a na konci súčet."
            : ""}
        </p>
        <div className="max-h-80 space-y-1 overflow-y-auto">
          {strom
            ? varianty.map((v, i) => (
                <div key={v.id} className="space-y-1">
                  <label className="flex cursor-pointer items-center gap-2 px-1 pt-1 text-sm font-medium">
                    <Zaskrtnutie
                      stav={stavVarianty(v)}
                      onChange={() => prepniVariantu(v)}
                      label={`${nazovVarianty(v)} ${texty.doCoho}`}
                    />
                    <span className="font-mono text-xs text-muted-foreground">
                      {i + 1}
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      {nazovVarianty(v)}
                      {v.id === otvorenaId ? (
                        <span className="font-normal text-muted-foreground">
                          {" "}
                          · otvorená
                        </span>
                      ) : null}
                    </span>
                    {!v.je_vyrobna && v.cena_s_dph != null ? (
                      <span className="shrink-0 text-xs font-normal text-muted-foreground">
                        {formatEur(v.cena_s_dph)}
                      </span>
                    ) : null}
                  </label>
                  <div className="space-y-1 pl-6">
                    {zostavyVarianty(v)}
                  </div>
                </div>
              ))
            : (() => {
                const v = varianty.find((x) => x.id === otvorenaId);
                return v ? zostavyVarianty(v) : [];
              })()}
        </div>
        {onExcel || onMail ? (
          <div className="flex flex-wrap items-center gap-2 border-t pt-3">
            {onExcel ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onExcel(vyberDokumentu())}
                disabled={exporting || nic}
                title="Tie isté riadky a súčty ako PDF — ceny ako čísla na kopírovanie"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Excel
              </Button>
            ) : null}
            {onMail ? (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => onMail(vyberDokumentu(), sExcelom)}
                  disabled={exporting || nic}
                  title="Stiahne e-mail s ponukou v prílohe — otvorí sa v Outlooku ako nový, adresu doplníš"
                >
                  <Mail className="h-4 w-4" />
                  E-mail (Outlook)
                </Button>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={sExcelom}
                    onChange={(e) => setSExcelom(e.target.checked)}
                    className="h-3.5 w-3.5 accent-primary"
                  />
                  do e-mailu aj Excel
                </label>
              </>
            ) : null}
          </div>
        ) : null}
        {bezCeny > 0 ? (
          // Vlastné položky z konfigurátora regálu bez ceny (KOLO 232) — inak
          // by išli zákazníkovi za 0 €
          <p className="text-xs text-amber-700 dark:text-amber-400" role="note" data-testid="ponuka-bez-ceny">
            {bezCeny} {sklonuj(bezCeny, ["položka", "položky", "položiek"])} tovaru{" "}
            {sklonuj(bezCeny, ["nemá", "nemajú", "nemá"])} cenu (doplň cenu) — v ponuke by{" "}
            {bezCeny === 1 ? "bola" : "boli"} za 0 €.
          </p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={exporting}
          >
            Zrušiť
          </Button>
          <Button
            type="button"
            onClick={() => onExport(vyberDokumentu())}
            disabled={exporting || nic}
          >
            <Download className="h-4 w-4" />
            {exporting
              ? "Generuje sa…"
              : vybraneVarianty.length > 1
                ? `Stiahnuť PDF (${vybraneVarianty.length} ${vybraneVarianty.length < 5 ? "varianty" : "variant"})`
                : vsetkyZostavy
                  ? "Stiahnuť PDF (všetky zostavy)"
                  : `Stiahnuť PDF (${vybrane.size} z ${pocetVsetkych})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
